





































import { serialize } from "class-transformer";
import { Component, Inject, Mixins, Watch } from "vue-property-decorator";
import { NavigationGuardNext, Route } from "vue-router";
import MaltaModule, { MODULE_ID, PORTAL_TARGET } from "../../index";
import BeforeUnload from "../../mixins/BeforeUnload";
import ProjectProposalMixin from "../../mixins/ProjectProposal";
import Utils from "../../mixins/Utils";
import FormDirtyOnLeavePage from "../private/forms/FormDirtyOnLeavePage.vue";
import { Form } from "../../models/Form";
import TdocFields from "../private/TdocFields.vue";
import { ProjectProposalPaths } from "../../models/ProjectProposal";

@Component({
    beforeRouteLeave(
        this: ProjectProposalFormB,
        to: Route,
        from: Route,
        next: NavigationGuardNext<Vue>
    ) {
        this.isDirty ? (this.showModal = true) : next();
    },
    components: {
        FormDirtyOnLeavePage,
        TdocFields
    }
})
export default class ProjectProposalFormB extends Mixins(
    ProjectProposalMixin,
    Utils,
    BeforeUnload
) {
    @Inject(MODULE_ID) maltaModule!: MaltaModule;

    response: Form | null = null;
    total = 0;
    loading = true;
    originalData = "";
    showModal = false;
    isDirty = false;
    isValid: string[] = [];
    endDate: string | null = "";
    totalFiles = 0;

    @Watch("response.file_ids", { immediate: true })
    @Watch("response.upload_urls", { immediate: true })
    onFiles() {
        if (this.response) {
            this.totalFiles = this.response.file_ids.length + this.response.upload_urls.length;
        }
    }

    @Watch("response", { deep: true })
    onRowsChange() {
        this.setBeforeUnload();
    }

    async loadData() {
    this.loading = true;

    const [data, err] = this.isFileUpload
        ? await this.maltaModule.projectProposalApi.getDocumentUpload(this.subjectId, this.applicationId, this.docCode)
        : await this.maltaModule.projectProposalApi.getApplicationDocument(this.subjectId, this.applicationId, this.docCode);

    if (err) {
        if (err.response && err.response.status !== 404) {
        this.goHomeGenericError();
        } else {
        const [resp, err2] = this.isFileUpload
            ? await this.maltaModule.projectProposalApi.getDocumentConfigUpload(this.subjectId, this.applicationId, this.docCode)
            : await this.maltaModule.projectProposalApi.getDocumentConfig(this.subjectId, this.applicationId, this.docCode);

        if (err2) {
            this.goHomeGenericError();
        } else {
            this.response = resp;
            this.originalData = serialize(resp); 
        }
        }
    } else if (data) {
        this.response = data;
        this.originalData = serialize(data);  
    }

    this.loading = false;
    }


    async mounted() {
        await this.loadData();
    }

    backToHome(force = false) {
        if (force) {
            this.unsetBeforeUnload();
            this.originalData = serialize(this.response);
        }
        this.isDirty = this.originalData !== serialize(this.response);
        this.goHome();
    }

    async saveAndGoBack() {
        if (!this.response) return;

        const noChangeInForm = this.originalData === serialize(this.response);
        if (noChangeInForm) {
            this.toast("No changes to save.", { type: "warn" });
            return;
        }

        const hasValue = (v: unknown): boolean => {
            if (v == null) return false;
            if (typeof v === "string") return v.trim().length > 0;
            if (Array.isArray(v)) return v.some(hasValue);
            if (typeof v === "object") return Object.values(v as Record<string, unknown>).some(hasValue);
            return true; // numbers/booleans/dates count as input
        };

        const pd: unknown = this.response.parsedData;
        let hasAnyFieldInput = false;

        if (Array.isArray(pd)) {
            hasAnyFieldInput = pd.some((row: unknown) => {
            if (row && typeof row === "object") {
                const obj = row as Record<string, unknown>;
                if ("value" in obj) return hasValue(obj.value);
                if ("values" in obj) return hasValue(obj.values);
                return Object.values(obj).some(hasValue);
            }
            return hasValue(row);
            });
        } else if (pd && typeof pd === "object") {
            hasAnyFieldInput = hasValue(pd);
        }

        const hasFiles = this.totalFiles > 0;

        if (!hasAnyFieldInput && !hasFiles) {
            this.isValid = ["_form_empty"];
            this.toast(this.i18n("form-not-valid").toString(), { type: "error" });
            return;
        }

        this.isValid = this.response.isValid;
        if (this.isValid.length !== 0) {
            this.toast(this.i18n("form-not-valid").toString(), { type: "error" });
            return;
        }

        const [resp, err] = this.response.documentId
            ? this.isFileUpload
            ? await this.maltaModule.projectProposalApi.setDocumentUpload(
                this.subjectId,
                this.applicationId,
                this.docCode,
                this.response.documentId,
                this.response.parsedUploadDocuments
                )
            : await this.maltaModule.projectProposalApi.setApplicationDocument(
                this.subjectId,
                this.applicationId,
                this.docCode,
                this.response.documentId,
                this.response.parsedData
                )
            : this.isFileUpload
            ? await this.maltaModule.projectProposalApi.createDocumentUpload(
                this.subjectId,
                this.applicationId,
                this.docCode,
                this.response.parsedUploadDocuments
                )
            : await this.maltaModule.projectProposalApi.createApplicationDocument(
                this.subjectId,
                this.applicationId,
                this.docCode,
                this.response.parsedData
                );

        if (err) {
            this.toast(this.i18n("something-went-wrong").toString(), { type: "error" });
            return;
        }

        this.unsetBeforeUnload();
        this.originalData = serialize(resp);
        this.response = resp;
        this.isDirty = false;
        this.$emit("reload");
        this.toast(this.i18n("saved-item").toString(), { type: "success" });
        this.goHome(true);
    }

    beforeDestroy() {
        this.unsetBeforeUnload();
    }

    get currentSection(): ProjectProposalPaths | undefined {
        const section = this.currentContract?.sections?.find((s: { code: string }) => s.code === this.$route.params.pageCode);
        return section;
    }

    get docCode(): string {
        return this.currentSection?.documentCode || '';
    }

    get portalTarget(): string {
        return PORTAL_TARGET;
    }
    get isFileUpload(): boolean {
        return this.currentSection?.fileUpload || false;
    }
    get currentTitle(): string {
        return this.currentSection?.title || '';
    }
    dynamicForm() {
    if (!this.contract?.dynamicForm) {
        this.goHome(); // Redirection to the home.
        return false; // Indicate the form should not be rendered.
    }
        return true; // Return true if `dynamicForm` exists.
    }
}
